import React from 'react';
import { PieChart, Percent, Briefcase } from 'lucide-react';

/**
 * Компонент структуры инвестиционного портфеля
 */
const PortfolioStructureSection = ({
  formData,
  handleInputChange,
  handleAssetAllocationChange,
  errors,
  portfolioTypes,
  assetTypes
}) => {
  const isCustomPortfolio = formData.portfolioType === 'custom';
  const totalAllocation = Object.values(formData.assetAllocation).reduce(
    (sum, val) => sum + (typeof val === 'number' ? val : 0), 
    0
  );

  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <PieChart className="w-5 h-5 mr-2 text-blue-500" />
        Структура портфеля
      </h3>

      {/* Тип портфеля */}
      <div className="mb-4">
        <label htmlFor="portfolioType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Тип портфеля
        </label>
        <select
          id="portfolioType"
          name="portfolioType"
          value={formData.portfolioType}
          onChange={handleInputChange}
          className="form-select w-full"
        >
          {portfolioTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name} - {type.description}
            </option>
          ))}
        </select>
      </div>

      {/* Распределение активов */}
      <div className="mb-4">
        <div className="flex justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Распределение активов
          </span>
          <span className={`text-sm font-medium ${Math.abs(totalAllocation - 100) > 0.01 ? 'text-red-500' : 'text-green-500'}`}>
            Всего: {totalAllocation.toFixed(1)}%
          </span>
        </div>

        {/* Отображение активов с ползунками */}
        <div className="space-y-3">
          {assetTypes.map((asset) => (
            <div key={asset.id} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-3 flex items-center">
                <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: asset.color }}></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{asset.name}</span>
              </div>
              <div className="col-span-7">
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.assetAllocation[asset.id] || 0}
                  onChange={(e) => handleAssetAllocationChange(asset.id, parseInt(e.target.value, 10))}
                  disabled={!isCustomPortfolio}
                  className="w-full"
                />
              </div>
              <div className="col-span-2">
                <div className="relative">
                  <input
                    type="text"
                    value={formData.assetAllocation[asset.id] || 0}
                    onChange={(e) => handleAssetAllocationChange(asset.id, parseInt(e.target.value, 10) || 0)}
                    disabled={!isCustomPortfolio}
                    className="form-input py-1 px-2 w-full text-sm"
                  />
                  <span className="absolute inset-y-0 right-2 flex items-center text-gray-500 pointer-events-none text-xs">
                    %
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {errors.assetAllocation && (
          <p className="mt-2 text-xs text-red-500 dark:text-red-400">
            {errors.assetAllocation}
          </p>
        )}
      </div>

      {/* Ожидаемая доходность */}
      <div>
        <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
          <Percent className="w-4 h-4 mr-1 text-blue-500" />
          Ожидаемая годовая доходность
        </div>

        {/* Настройки ожидаемой доходности */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {assetTypes.map((asset) => (
            <div key={asset.id} className="flex items-center">
              <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: asset.color }}></div>
              <label htmlFor={`expectedReturns.${asset.id}`} className="text-sm text-gray-700 dark:text-gray-300 w-24">
                {asset.name}:
              </label>
              <div className="relative flex-grow">
                <input
                  type="text"
                  id={`expectedReturns.${asset.id}`}
                  name={`expectedReturns.${asset.id}`}
                  value={formData.expectedReturns[asset.id]}
                  onChange={handleInputChange}
                  className={`form-input py-1 text-sm w-full ${errors[`expectedReturns.${asset.id}`] ? 'input-error' : ''}`}
                />
                <span className="absolute inset-y-0 right-2 flex items-center text-gray-500 pointer-events-none text-xs">
                  %
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Ошибки доходности */}
        {assetTypes.some(asset => errors[`expectedReturns.${asset.id}`]) && (
          <div className="mt-2 space-y-1">
            {assetTypes.map(asset => errors[`expectedReturns.${asset.id}`] && (
              <p key={asset.id} className="text-xs text-red-500 dark:text-red-400">
                {asset.name}: {errors[`expectedReturns.${asset.id}`]}
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Информационный блок */}
      <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-800/30 rounded-lg border border-blue-200 dark:border-blue-700">
        <div className="flex items-start">
          <Briefcase className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300">
              Рекомендации по структуре портфеля
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
              На российском рынке рекомендуется диверсифицировать активы между российскими и зарубежными бумагами. 
              Для защиты от валютных рисков часть портфеля можно держать в активах, номинированных в иностранной валюте.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioStructureSection;